import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Checkbox, Pagination, Tag, ToastContainer } from "pepsico-ds";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../../components/common/listingComponents/ListingComponentContext";
import Table from "../../../components/common/table/Table";
import getListPartners from "../../../components/partners/graphql/queries/listPartners";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { filterCatalogs } from "../../../graphql/queries/getCatalogs";
import { statusColor } from "../../../pages/RewardsCatalogs/utils/statusColor";
import {
  capitalizeFirstLetterOfEachWord,
  getDateWithFormat,
} from "../../../utils/commons";
import "./ListRewardCatalogs.scss";

/**
 * Component for listing reward catalogs with selection
 */
export const ListRewardCatalogs = (props) => {
  const { data, path, handleChange } = props;

  // State for selected catalogs - initialize as empty array
  const [selectedCatalogs, setSelectedCatalogs] = useState(data || []);

  // Update the parent component when selections change
  useEffect(() => {
    if (!handleChange || !path) return;
    // Extract only the catalog IDs
    const catalogIds = selectedCatalogs.map((catalog) => catalog);

    // Notify parent component of changes with the correct property name

    try {
      // For JsonForms, we need to update the specific property in the schema
      // The path should point to the reward_ids property
      handleChange(path, catalogIds);
    } catch (error) {
      console.error("Error updating form data:", error);
    }
  }, [selectedCatalogs, handleChange, path]);

  const ROWS_PER_PAGE = 8;

  return (
    <div className="list-reward-catalogs">
      <ListingComponentProvider
        query={filterCatalogs}
        fetchPolicy="cache-and-network"
        queryKey="filterCatalogs"
        extraVariables={{ sort: ["created_date", "DESC"] }}
        initialFilterData={{
          catalogName: "",
          status: [],
          partnerId: [],
          selectableOnly: true,
          limit: ROWS_PER_PAGE,
          offset: 0,
        }}
        noPagination={true}
      >
        <RewardsCatalogsSelectionList
          rowsPerPage={ROWS_PER_PAGE}
          selectedCatalogs={selectedCatalogs}
          setSelectedCatalogs={setSelectedCatalogs}
        />
      </ListingComponentProvider>
    </div>
  );
};

const RewardsCatalogsSelectionList = ({
  rowsPerPage,
  selectedCatalogs,
  setSelectedCatalogs,
}) => {
  const {
    filterData,
    setFilterData,
    viewableRecords,
    error: filterCatalogError,
  } = useContext(ListingComponentContext);

  const [toastData, setToastData] = useState([]);

  const { data: partnersData, error: partnersError } = useQuery(getListPartners, {
    variables: { filter: {} },
  });

  const partnersList = useMemo(
    () => partnersData?.listPartners?.items || [],
    [partnersData]
  );

  // Error handling for queries
  useEffect(() => {
    const errors = [
      { error: filterCatalogError, message: "Catalogs data error." },
      { error: partnersError, message: "Partners data error." },
    ];

    const newToasts = errors
      .filter(({ error }) => !!error)
      .map(({ error, message }, index) => ({
        type: "error",
        text: error?.message ? `${message} (${error.message})` : message,
        key: `toast-${message}-${index}-${Date.now()}`, // Add unique key for each toast
      }));

    if (newToasts.length > 0) {
      setToastData((prev) => [...prev, ...newToasts]);
    }
  }, [filterCatalogError, partnersError]);

  const getPartnerNameById = useCallback(
    (id) => {
      const partner = partnersList.find((partner) => partner.id === id);
      return partner ? partner.name : "-";
    },
    [partnersList]
  );

  const { programTimezone } = useContext(GlobalConfigContext) || {};

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (filterData) {
      setCurrentPage(Math.ceil(filterData.offset / rowsPerPage) + 1);
    }
  }, [filterData, rowsPerPage]);

  const totalPages = Math.ceil((viewableRecords?.total || 0) / rowsPerPage);

  const handleChangePage = useCallback(
    (page) => {
      setFilterData((prev) => ({
        ...prev,
        offset: (page - 1) * rowsPerPage,
      }));
      setCurrentPage(page);
    },
    [setFilterData, rowsPerPage]
  );

  // Utility function to manage catalog selections
  const updateCatalogSelections = useCallback(
    (action, catalogIds = []) => {
      let catalogId;

      switch (action) {
        case "toggle":
          // Toggle a single catalog ID
          catalogId = catalogIds[0];
          console.log("Toggling catalog ID:", catalogId);
          setSelectedCatalogs((prev) => {
            // Check if catalog ID is already selected
            const isSelected = prev.includes(catalogId);
            if (isSelected) {
              // Remove catalog ID from selection
              return prev.filter((id) => id !== catalogId);
            } else {
              // Add catalog ID to selection
              return [...prev, catalogId];
            }
          });
          break;

        case "clear":
          // Clear all selections
          setSelectedCatalogs([]);
          break;

        case "addMultiple":
          // Add multiple catalog IDs, avoiding duplicates
          setSelectedCatalogs((prev) => {
            const newSelections = catalogIds.filter((id) => !prev.includes(id));
            return [...prev, ...newSelections];
          });
          break;

        default:
          console.warn("Unknown catalog selection action:", action);
      }
    },
    [setSelectedCatalogs]
  );

  // Handle checkbox selection for a single catalog ID
  const handleSelectCatalog = useCallback(
    (catalogId) => {
      updateCatalogSelections("toggle", [catalogId]);
    },
    [updateCatalogSelections]
  );

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (!viewableRecords?.items?.length) return;

    // If all items are already selected, unselect all
    const allSelected = viewableRecords.items.every((row) =>
      selectedCatalogs.includes(row.catalogId)
    );

    if (allSelected) {
      // Unselect all items
      updateCatalogSelections("clear");
    } else {
      // Select all items - get all catalog IDs
      const allCatalogIds = viewableRecords.items.map((row) => row.catalogId);
      updateCatalogSelections("addMultiple", allCatalogIds);
    }
  };

  // Check if all visible items are selected
  const areAllSelected = useMemo(() => {
    if (!viewableRecords?.items?.length) return false;
    return viewableRecords.items.every((row) =>
      selectedCatalogs.includes(row.catalogId)
    );
  }, [viewableRecords, selectedCatalogs]);

  // Add selection column to the headers with select all checkbox
  const catalogsHeaders = [
    {
      columnKey: "selection",
      header: (
        <Checkbox
          checked={areAllSelected}
          onChange={handleSelectAll}
          data-testid="checkbox-select-all"
        />
      ),
    },
    {
      columnKey: "catalogId",
      header: "ID",
    },
    {
      columnKey: "catalogName",
      header: "Name",
    },
    {
      columnKey: "type",
      header: "Type",
    },
    {
      columnKey: "partnerId",
      header: "Rewards Partner",
    },
    {
      columnKey: "createdDate",
      header: "Created Date",
    },
    {
      columnKey: "status",
      header: "Status",
    },
  ];

  const renderRows = useMemo(
    () =>
      viewableRecords?.items?.map((row) => ({
        selection: (
          <Checkbox
            checked={selectedCatalogs.includes(row.catalogId)}
            onChange={() => handleSelectCatalog(row.catalogId)}
            data-testid={`checkbox-${row.catalogId}`}
            key={`${row.catalogId}`}
          />
        ),
        catalogId: row?.catalogId || "-",
        catalogName: row?.catalogName ?? "-",
        type: row?.type ? row.type.replace("_", " ") : "-",
        createdDate: row?.createdDate
          ? getDateWithFormat(row.createdDate, programTimezone)
          : "-",
        partnerId: row?.partnerId ? getPartnerNameById(row.partnerId) : "-",
        status: row?.status ? (
          <Tag
            key={`tag-${row.catalogId}`}
            color={statusColor(row.status)}
            size="large"
            text={capitalizeFirstLetterOfEachWord(row.status)}
            isCopyable={false}
          />
        ) : (
          "-"
        ),
      })) || [],
    [
      viewableRecords,
      programTimezone,
      getPartnerNameById,
      selectedCatalogs,
      handleSelectCatalog,
    ]
  );

  return (
    <div className="reward-catalogs-selection-list">
      {Array.isArray(toastData) && toastData?.length > 0 && (
        <ToastContainer
          className="catalogs-listing-toast"
          data={toastData}
          removeToast={() => setToastData([])}
          showActionIcon
        />
      )}

      <div className="selected-count">
        Selected: {selectedCatalogs.length} catalog(s)
      </div>

      <div className="table-container">
        <Table headerData={catalogsHeaders} rowData={renderRows} />
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onUpdate={handleChangePage}
          pageCount={totalPages}
          size="medium"
          variant="number"
          data-testid="pagination"
        />
      )}
    </div>
  );
};

/**
 * Export the component for direct use
 */
export default ListRewardCatalogs;
export const ListRewardCatalogsControl =
  withJsonFormsControlProps(ListRewardCatalogs);
